import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  Power,
  Flame,
  BarChart as ChartBar,
  Rocket,
  Shield,
  Target,
  Zap,
  TrendingUp,
  Users,
  Menu,
  X,
  Mail,
  LineChart,
  Share2,
  ChevronLeft,
  ChevronRight,
  Bot,
  MessageSquare,
  BarChart
} from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import ContactModal from './components/ContactModal';
import DownloadGuideForm from './components/DownloadGuideForm';
import StatsSection from './components/StatsSection';
import FAQSection from './components/FAQSection';
import ChatBot from './components/ChatBot';
import Legal from './pages/Legal';
import Processus from './pages/processus';

const clientLogos = [
  { name: "Microsoft", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823072/2_vhv3rl.png" },
  { name: "Google", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823072/1_yvuwdg.png" },
  { name: "Amazon", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823073/3_ytsuon.png" },
  { name: "Apple", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823073/4_vq48xe.png" },
  { name: "Netflix", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823074/5_munm0c.png" },
  { name: "Meta", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823077/8_srpotx.png" },
  { name: "IBM", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823077/9_exwxvj.png" },
  { name: "Intel", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823078/10_bjnlec.png" },
  { name: "Samsung", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823079/11_ylrzku.png" },
  { name: "Oracle", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823080/13_gyhi0h.png" },
  { name: "Salesforce", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823080/12_w6odzy.png" },
  { name: "Adobe", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823082/14_dqmkpg.png" },
  { name: "Cisco", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823082/16_dbkbg3.png" },
  { name: "SAP", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823082/15_vufcl2.png" },
  { name: "VMware", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823083/17_kjahkw.png" },
  { name: "Nvidia", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823085/20_rfgxtk.png" },
  { name: "Dell", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823085/19_qx0ktr.png" },
  { name: "HP", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823075/6_nnghov.png" },
  { name: "Twitter", url: "https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto/q_auto/v1744823076/7_x0qwk4.png" },
];

const problems = [
  {
    title: "Manque de visibilité digitale",
    description: "Dans un monde numérique saturé, se démarquer devient de plus en plus difficile. Les approches traditionnelles ne suffisent plus pour atteindre et engager votre audience cible."
  },
  {
    title: "Données inexploitées",
    description: "Vous disposez d'une mine d'or de données clients, mais leur exploitation manuelle est chronophage et peu efficace, limitant votre capacité à prendre des décisions éclairées."
  },
  {
    title: "ROI Marketing insuffisant",
    description: "Vos investissements marketing ne génèrent pas les résultats escomptés, avec des campagnes qui peinent à convertir et un coût d'acquisition client trop élevé."
  }
];

const solutions = [
  {
    icon: Brain,
    title: "Stratégie IA & Marketing",
    advantage: "Intelligence artificielle au service de votre marketing",
    description: "Des campagnes ultra-ciblées, intelligentes et évolutives."
  },
  {
    icon: Zap,
    title: "Automatisation intelligente",
    advantage: "Optimisation des processus marketing",
    description: "Nous transformons vos flux marketing pour plus de productivité et moins de friction."
  },
  {
    icon: Target,
    title: "Optimisation continue",
    advantage: "Amélioration permanente des performances",
    description: "Nos campagnes s'optimisent en temps réel selon vos KPI."
  },
  {
    icon: ChartBar,
    title: "Analyse de données",
    advantage: "Insights data-driven",
    description: "Mesurez, analysez, itérez : le cercle vertueux de la performance."
  },
  {
    icon: Rocket,
    title: "Croissance accélérée",
    advantage: "Boost de votre développement",
    description: "Développez votre chiffre d'affaires grâce à l'IA et au marketing digital."
  },
  {
    icon: Shield,
    title: "Sécurité & conformité",
    advantage: "Protection des données garantie",
    description: "Vos données sont protégées, votre image de marque préservée."
  }
];

const marketingTools = [
  {
    icon: Bot,
    title: "Identifiez et ciblez les bonnes audiences",
    description: "L’IA analyse les signaux digitaux pour maximiser votre visibilité."
  },
  {
    icon: MessageSquare,
    title: "Activez vos campagnes automatiquement",
    description: "Emails, SMS et contenus générés par l’IA, alignés avec votre marque."
  },
  {
    icon: BarChart,
    title: "Pilotez vos performances en temps réel",
    description: "Suivez l’impact de vos actions et ajustez votre marketing en continu."
  }
];

const testimonials = [
  {
    name: "Rodolphe Gardies",
    role: "Co‑fondateur et responsable Marketing, Asphalte",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1744993679/RD_tx2qjb.jpg?auto=format&fit=crop&q=80",
    quote: "Dès le tout début, Nova a créé notre site e‑commerce, qui a immédiatement rencontré un énorme succès et posé les bases de notre croissance.",
  },
  {
    name: "Philippe Ditisheim",
    role: "CEO, People First Technologies",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1744993679/PD_hybdbv.jpg?auto=format&fit=crop&q=80",
    quote: "Nova pilote notre communication en France et aux USA et a participé à la création de notre chatbot IA : un duo gagnant qui renforce notre relation client au quotidien. ",
  },
  {
    name: "Cécile Dejoux",
    role: "Directrice, Le Cnam (LLHC)",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1744993679/CD_nocdrk.jpg?auto=format&fit=crop&q=80",
    quote: "Depuis six ans, Nova nous accompagne dans l'acculturation à l'IA et assure toute la communication du Learning Lab Human Change & Future of Work ; les résultats sur l'engagement sont remarquables. ",
  },
  {
    name: "Steeve Baudouin",
    role: "PDG, N1  Immobilier",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1744993679/SB_ukp7fo.jpg?auto=format&fit=crop&q=80",
    quote: "Nova gère notre communication digitale, le web, le CRM et le reporting en France et en Espagne, dans un environnement tech exigeant ; le retour sur investissement est tangible..",
  },
  {
    name: "Grégory Vignola",
    role: "Co‑fondateur, Blue Cult",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1744992455/GV_hmiuom.jpg?auto=format&fit=crop&q=80",
    quote: " En créant notre site e‑commerce et en déployant un programme de fidélisation sur‑mesure, Nova a renforcé la loyauté de notre clientèle et boosté nos ventes.",
  },
  {
    name: "Madeleine Houbart",
    role: "Secrétaire Générale, Afex",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1745241068/MH_copie_gy3s4w.jpg?auto=format&fit=crop&q=80",
    quote: "Depuis le lancement, Nova a développé notre plateforme, vitrine de l'architecture française dans le monde, qui nous offre une visibilité mondiale inégalée.",
  },
  {
    name: "Thibault Poujade",
    role: "PDG, Valorea Madagascar",
    image: "https://res.cloudinary.com/doo9fgw4x/image/upload/v1789626824/thibault-poujade-DaFMBOR8_hhago1.jpg",
    quote: "Nova nous accompagne dans la structuration de notre communication digitale et la valorisation de notre impact environnemental. Leur approche allie stratégie, technologie et efficacité opérationnelle.",
  }
];

const navigation = [
  { name: 'Processus', href: '/processus' },
  { name: 'Enjeux', href: '#problems' },
  { name: 'Solutions', href: '#solutions' },
  { name: 'Marketing', href: '#marketing' },
{ name: 'Glossaire IA', href: '/ia-marketing.html' },
  { name: 'Témoignages', href: '#testimonials' },
  { name: 'Contact', href: '#contact' },
];

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentTestimonialPage, setCurrentTestimonialPage] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setScrollPosition((prev) => (prev + 1) % (clientLogos.length * 200));
    }, 30);

    return () => clearInterval(interval);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const maxTestimonialPage = Math.ceil(testimonials.length / 2) - 1;

  const nextTestimonials = () => {
    setCurrentTestimonialPage((prev) => 
      prev >= maxTestimonialPage ? 0 : prev + 1
    );
  };

  const previousTestimonials = () => {
    setCurrentTestimonialPage((prev) => 
      prev <= 0 ? maxTestimonialPage : prev - 1
    );
  };

  const visibleTestimonials = testimonials.slice(currentTestimonialPage * 2, (currentTestimonialPage * 2) + 2);

  return (
    <Router>
      <Routes>
        <Route path="/processus" element={<Processus />} />
        <Route path="/mentions-legales" element={<Legal />} />
        <Route path="/" element={
          <div className="min-h-screen bg-black text-white">
            <ContactModal 
              isOpen={isContactModalOpen} 
              onClose={() => setIsContactModalOpen(false)} 
            />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
              <div className="container mx-auto px-8 py-4 flex justify-between items-center">
                <div className="flex flex-col leading-tight">
                  <a href="#" className="flex flex-col">
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-white">nova</span>
                      <span className="text-2xl font-bold text-blue-500">communication</span>
                    </div>
                    <span className="text-xs text-gray-400 tracking-wide">
                      — by KEROS MANAGEMENT
                    </span>
                  </a>
                </div>
                
                {/* Desktop Navigation */}
                <ul className="hidden md:flex space-x-8">
  {navigation.map((item) => (
    <li key={item.name}>
<a
  href={item.href}
  onClick={(e) => {
    if (item.href === "/ia-marketing.html") {
      e.preventDefault();
      window.location.href = "/ia-marketing.html";
    }
  }}
  className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
>
  {item.name}
</a>
    </li>
  ))}
</ul>

                {/* Mobile Menu Button */}
                <button 
                  className="md:hidden text-gray-300 hover:text-white transition-colors"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>

              {/* Mobile Navigation */}
              {isMobileMenuOpen && (
                <div className="md:hidden bg-black/95 border-b border-gray-800">
                  <div className="container mx-auto px-8 py-4">
                    <ul className="space-y-4">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            className="block text-gray-300 hover:text-blue-400 transition-colors duration-200 py-2"
                            onClick={closeMobileMenu}
                          >
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex">
              <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
              </div>

              <div className="container mx-auto px-4 z-10 flex items-center pt-20 pb-8">
                <div className="max-w-4xl py-8 leading-[1.15] overflow-visible">
                  {/* Titre animé avec meilleure responsivité */}
                  <div className="space-y-2 mb-3">
                    <motion.h1
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: 0.8,
                        ease: [0.25, 0.1, 0.25, 1],
                        delay: 0.2
                      }}
                      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 leading-[1.1]"
                    >
                      Transformez votre croissance
                    </motion.h1>

                    <motion.h1
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: 0.8,
                        ease: [0.25, 0.1, 0.25, 1],
                        delay: 0.4
                      }}
                      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 leading-[1.1] pb-[4px]"
                    >
                      digitale avec l'IA
                    </motion.h1>
                  </div>

                  {/* Bloc SEO friendly */}
                  <p className="text-sm md:text-base text-gray-400 max-w-2xl mb-6 leading-snug">
                    Stratégies de Marketing digital propulsées par l'IA pour PME et Grandes entreprises exigeantes
                  </p>

                  {/* Sous-titre */}
           <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mt-12 mb-12">
  Marketing digital pour les marques et entreprises exigeantes.
</p>

                  {/* Points clés avec meilleure responsivité */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-12">
                    <div className="flex items-start gap-3 md:gap-4 leading-tight">
                      <ChartBar className="w-6 h-6 md:w-8 md:h-8 text-blue-400 flex-shrink-0 mt-1" />
                      <p className="text-sm md:text-base text-gray-300">
                        <strong className="text-blue-400">Boostez votre ROI :</strong> Optimisation des<br className="hidden sm:block" />
                        campagnes publicitaires par l'IA
                      </p>
                    </div>

                    <div className="flex items-start gap-3 md:gap-4 leading-tight">
                      <Rocket className="w-6 h-6 md:w-8 md:h-8 text-blue-400 flex-shrink-0 mt-1" />
                      <p className="text-sm md:text-base text-gray-300">
                        <strong className="text-blue-400">Automatisation intelligente :</strong> Gains de<br className="hidden sm:block" />
                        temps et efficacité de vos flux
                      </p>
                    </div>

                    <div className="flex items-start gap-3 md:gap-4 leading-tight">
                      <Flame className="w-6 h-6 md:w-8 md:h-8 text-blue-400 flex-shrink-0 mt-1" />
                      <p className="text-sm md:text-base text-gray-300">
                        <strong className="text-blue-400">Personnalisation :</strong> la bonne offre,<br className="hidden sm:block" />
                        au bon moment, pour chaque client
                      </p>
                    </div>

                    <div className="flex items-start gap-3 md:gap-4 leading-tight">
                      <Power className="w-6 h-6 md:w-8 md:h-8 text-blue-400 flex-shrink-0 mt-1" />
                      <p className="text-sm md:text-base text-gray-300">
                        <strong className="text-blue-400">Moteurs de réponses :</strong> mise en avant<br className="hidden sm:block" />
                        et recommandations
                      </p>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold flex items-center gap-2 transition-all"
                  >
                    Demandez votre consultation gratuite
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>
              </div>

        {/* Right side image - CLEAN FINAL */}
<div className="absolute top-0 right-0 w-1/2 h-full hidden md:block overflow-hidden">

<motion.img
  src="https://images.unsplash.com/photo-1689876673591-215e6f1b8a31?q=80&w=2487&auto=format&fit=crop"
  className="w-full h-full object-cover"
animate={{ scale: [1, 1.06], y: [0, -6] }}
  transition={{
    duration: 3.2,
    ease: "easeInOut",
    repeat: Infinity,
    repeatType: "reverse"
  }}
/>
</div>
              
            </section>

            {/* Logo Carousel Section - Reduced padding */}
            <section className="py-6 bg-black/50 backdrop-blur-sm border-y border-gray-800">
              <div className="container mx-auto px-4">
                <p className="text-center text-gray-400 mb-4 text-sm md:text-base">
                  Ils nous font confiance
                </p>
                <div className="relative overflow-hidden">
                  <div 
                    className="flex items-center gap-6 md:gap-12 whitespace-nowrap"
                    style={{
                      transform: `translateX(-${scrollPosition}px)`,
                      transition: 'transform 0.5s linear',
                    }}
                  >
                    {/* Duplicate the logos array to create a seamless loop */}
                    {[...clientLogos, ...clientLogos].map((logo, index) => (
                      <div key={index} className="flex-shrink-0">
                        <img
                          src={logo.url}
                          alt={logo.name}
                          loading="lazy"
                          onLoad={(e) => e.currentTarget.classList.add('opacity-50')}
                          className="h-12 md:h-20 w-auto object-contain opacity-0 hover:opacity-100 transition-opacity duration-700 ease-in-out filter invert"
                        />
                      </div>
                    ))}
                  </div>
                  {/* Gradient overlays */}
                  <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-black to-transparent"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-l from-black to-transparent"></div>
                </div>
              </div>
            </section>

            {/* Problems Section */}
            <section id="problems" className="py-24 bg-gradient-to-b from-black to-gray-900">
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold mb-6 text-center">
                  Arrêtez de lutter, commencez à innover
                </h2>
                <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
                  Découvrez comment nous transformons les défis du marketing digital en opportunités de croissance grâce à l'intelligence artificielle.
                </p>
                <div className="grid md:grid-cols-3 gap-8">
                  {problems.map((problem, index) => (
                    <div 
                      key={index} 
                      className="card"
                    >
                      <h3 className="text-xl font-semibold mb-4">{problem.title}</h3>
                      <p className="text-gray-400">{problem.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Solutions Section */}
            <section id="solutions" className="py-24 bg-black">
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold mb-6 text-center">
                  Les solutions de Marketing digital et d'IA de Nova.
                </h2>
                <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
                  Chez Nova. nous comprenons les défis complexes auxquels sont confrontées les PME et les grandes entreprises pour développer efficacement leur présence digitale.
                </p>
                <div className="grid md:grid-cols-2 gap-8">
                  {solutions.map((solution, index) => (
                    <div 
                      key={index} 
                      className="card group"
                    >
                      <solution.icon className="w-12 h-12 text-blue-400 mb-6 transition-transform duration-300 group-hover:scale-110" />
                      <h3 className="text-xl font-semibold mb-2 transition-colors duration-300 group-hover:text-blue-400">{solution.title}</h3>
                      <p className="text-blue-400 font-medium mb-4">{solution.advantage}</p>
                      <p className="text-gray-400">{solution.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Marketing Tools Section */}
            <section id="marketing" className="py-24 bg-gradient-to-br from-gray-900 to-black">
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold mb-6">
                  Nos agents marketing autonomes
                </h2>
               <div className="grid md:grid-cols-2 gap-12 items-center">

  <div className="space-y-8">
    {marketingTools.map((tool, index) => (
      <div key={index} className="flex gap-4">
        <tool.icon className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
        <div>
          <h3 className="text-xl font-semibold mb-2 text-blue-400">{tool.title}</h3>
          <p className="text-gray-400">{tool.description}</p>
        </div>
      </div>
    ))}

<button
  onClick={() => {
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }}
  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all mt-6 hover:scale-105"
>
  Passer en mode Agent
  <ArrowRight className="w-4 h-4" />
</button>
  </div>

  {/* IMAGE */}
<div className="relative">
  <motion.div
    className="relative rounded-xl overflow-hidden"
    animate={{ scale: [1, 1.08, 1] }}
    transition={{
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    <img
      src="https://res.cloudinary.com/doo9fgw4x/image/upload/f_auto,q_auto/v1744708319/Outils_Marketing_os7hs5.webp"
      alt="Marketing Tools"
      className="w-full h-auto block"
    />

    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
  </motion.div>
</div>

</div> {/* ← fermeture du grid */}

</div> {/* ← fermeture du container parent (IMPORTANT) */}

</section> {/* ← fermeture section marketing */}

{/* Testimonials Section */}
<section id="testimonials" className="py-24 bg-gradient-to-b from-gray-900 to-black">
            
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold mb-6 text-center">
                  Ne nous croyez pas sur parole
                </h2>
                <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
                  Découvrez les succès de nos clients en marketing digital et IA
                </p>
                <div className="relative">
                  <div className="grid md:grid-cols-2 gap-8">
                    {visibleTestimonials.map((testimonial, index) => (
                      <div 
                        key={index} 
                        className="card"
                      >
                        <div className="flex items-center gap-4 mb-6">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          <div>
                            <h3 className="font-semibold">{testimonial.name}</h3>
                            <p className="text-gray-400">{testimonial.role}</p>
                          </div>
                        </div>
                        <p className="text-gray-300 italic">"{testimonial.quote}"</p>
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  <button 
                    onClick={previousTestimonials}
                    className="absolute left-[-2rem] top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 rounded-full text-white transition-all duration-300 hover:scale-110"
                    aria-label="Témoignages précédents"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <button 
                    onClick={nextTestimonials}
                    className="absolute right-[-2rem] top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 rounded-full text-white transition-all duration-300 hover:scale-110"
                    aria-label="Témoignages suivants"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* Pagination Dots */}
                  <div className="flex justify-center gap-2 mt-8">
                    {Array.from({ length: Math.ceil(testimonials.length / 2) }).map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonialPage(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          currentTestimonialPage === index ? 'bg-blue-500 w-4' : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                        aria-label={`Page de témoignages ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section id="contact" className="py-24 bg-gradient-to-br from-blue-900 to-black">
              <div className="container mx-auto px-4 text-center">
                <h2 className="text-4xl font-bold mb-6">
                  Prêt à transformer votre Marketing avec l'IA ?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8">
                  Demandez dès maintenant votre consultation gratuite et découvrez comment notre expertise en marketing digital et IA peut propulser votre croissance.
                </p>
                <button 
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 mx-auto transition-all"
                >
                  Obtenir ma consultation gratuite
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </section>

            {/* Stats Section */}
            <StatsSection />

            {/* FAQ Section */}
            <FAQSection />

            {/* Download Guide Section */}
            <section className="py-24 bg-black">
              <div className="container mx-auto px-4">
                <DownloadGuideForm />
              </div>
            </section>

            {/* Chat Bot */}
            <ChatBot />

            {/* Footer */}
            <footer className="py-12 bg-black border-t border-gray-800">
              <div className="container mx-auto px-4">
                <div className="grid md:grid-cols-4 gap-8">
                  <div>
                    <h3 className="font-bold mb-4">Nova Communication</h3>
                    <p className="text-gray-400">Votre partenaire en Marketing digital et IA</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Contact</h4>
                    <ul className="text-gray-400 space-y-2">
                      <li>contact@kerosmangement.ie</li>
                      <li>+33 189 480 425</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-4">Légal</h4>
                    <ul className="text-gray-400 space-y-2">
                      <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                      <li>Politique de confidentialité</li>
                      <li>CGV</li>
                    </ul>
                  </div>
                  <div>    
                    <h4 className="font-semibold mb-4">KEROS Management LTD</h4>
                    <address className="text-gray-400 text-sm not-italic space-y-1">
                      <p>Pod 2, The Old Station House</p>
                      <p>15a main street, Blackrock, Co Dublin</p>
                      <p>A94 T8P8, Ireland</p>
                    </address>
                  </div>
                </div>
                <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400">
                  <p>© 2025 Nova Communication. Tous droits réservés.</p>
                </div>
              </div>
            </footer>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;